/*!
 * Copyright Optimus Ride 2020
 */

import { UserServiceClient } from '../../grpc/UserServiceClientPb';
import {
  GetApprovalStatusResponse,
  UpdateApprovalStatusRequest,
  UpdateApprovalStatusResponse,
  UpdateUserSummaryRequest,
  UserApprovalStatus,
  UserId,
  UserProfile,
  UserProfileResponse,
  UserType,
  UsersRequest,
  UpdateUserSummaryResponse,
  CreateOwnUserRequest,
  RequestSiteAccessRequest,
  PushTokenType,
  UpdatePushTokenRequest,
  UpdatePushTokenResponse
} from '../../grpc/user_pb';
import { Empty, Address } from '../../grpc/shared_pb';
import { GRPC_USER_PORT } from '../../constants';
import Auth from '../../auth/abstract-auth';
import { CONSOLE_ERROR_MSG, requestMetadata } from '../util';
import { isPhone, isEmail } from '@optimusride/client-library/utils';
import { ClientReadableStream } from 'grpc-web';

export interface UpdateUserSummaryRequestData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  allowText?: boolean;
}

export default class UserServiceConsumer {
  private userServiceClient: UserServiceClient;
  private auth: Auth;
  private oriApiKey: undefined | string;

  constructor(auth: Auth, hostname: string, oriApiKey?: undefined | string) {
    this.auth = auth;
    this.oriApiKey = oriApiKey;
    this.userServiceClient = new UserServiceClient(`${hostname}:${GRPC_USER_PORT}`, null, null);
  }

  public getUserType = (): Promise<UserType> => 
    new Promise(async (resolve, reject) => {
      this.userServiceClient.getUserType(
        new Empty(),
        await requestMetadata(this.auth, this.oriApiKey), 
        (err, response) => {
          if (!err) {
            const userType = response.getUsertype();
            if (response.hasError()) {
              reject(response.getError());
            } else if (response.hasUsertype() && userType) {
              resolve(userType);
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
          } else {
            reject(err);
          }
        });
    });

  public getUserProfile = (): Promise<UserProfile> => 
    new Promise(async (resolve, reject) => {
      this.userServiceClient.getUserProfile(
        new Empty(),
        await requestMetadata(this.auth, this.oriApiKey),
        (err, response) => {
          if (!err) {
            const profile = response.getUserprofile();
            if (response.hasError()) {
              reject(response.getError());
            } else if (response.hasUserprofile() && profile) {
              resolve(profile);
            } else {
              throw new Error(CONSOLE_ERROR_MSG);
            }
          } else {
            reject(err);
          }
        });
    });

  public updateUserSummary = (requestData: UpdateUserSummaryRequestData): Promise<UpdateUserSummaryResponse> => {
    const request = new UpdateUserSummaryRequest();
    if (requestData.firstName) {
      request.setFirstName(requestData.firstName);
    }
    if (requestData.lastName) {
      request.setLastName(requestData.lastName);
    }
    if (requestData.email) {
      request.setEmail(requestData.email);
    }
    if (requestData.phone) {
      request.setPhone(requestData.phone);
    }
    if (requestData.allowText === false || requestData.allowText === true) { // Can be null
      request.setAllowText(requestData.allowText);
      request.setHasAllowText(true);
    }

    return new Promise(async (resolve, reject) => {
      this.userServiceClient.updateUserSummary(
        request,
        await requestMetadata(this.auth, this.oriApiKey),
        (err, response) => {
          if (!err) {
            resolve(response);
          } else {
            reject(err);
          }
        });
    });
  }

  public updateUserAddress = (address: Address): Promise<boolean> => 
    new Promise(async (resolve, reject) => {
      this.userServiceClient.updateUserAddress(address, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          if (response.hasError()) {
            reject(response.getError());
          } else if (response.hasEmpty()) {
            resolve(true);
          } else {
            throw new Error(CONSOLE_ERROR_MSG);
          }
        } else {
          reject(err);
        }
      });
    });

  public getUsersByOperatingArea = (operatingAreaId: number, cursor: number): Promise<UserProfile[]> => 
    new Promise(async (resolve, reject) => {
      const users = new Array();
      const request = new UsersRequest();
      request.setOperatingAreaId(operatingAreaId);
      request.setCursor(cursor);

      const stream = this.userServiceClient.getUsersByOperatingArea(request, await requestMetadata(this.auth, this.oriApiKey)) as ClientReadableStream<UserProfileResponse>;
      stream.on('data', (response: UserProfileResponse) => {
        if (response.hasError()) {
          reject();
        } else if (response.hasUserprofile()) {
          users.push(response.getUserprofile());
        } else {
          throw new Error(CONSOLE_ERROR_MSG);
        }
      });

      stream.on('status', status => {
        if (status.code === 0 || status.code === 14) {
          resolve(users);
        } else {
          reject();
        }
      });
    });

  public getUserApprovalStatus = (): Promise<GetApprovalStatusResponse> => 
    new Promise(async (resolve, reject) => {
      this.userServiceClient.getApprovalStatus(new Empty(), await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          resolve(response);
        } else {
          reject(err);
        }
      });
    });

  public updateUserApprovalStatus = (userId: number, operatingAreaId: number, approvalStatus: UserApprovalStatus): Promise<UpdateApprovalStatusResponse> => 
    new Promise(async (resolve, reject) => {
      const request = new UpdateApprovalStatusRequest();
      const user = new UserId;
      user.setId(userId);
      request.setUserId(user);
      request.setOperatingAreaId(operatingAreaId);
      request.setApprovalStatus(approvalStatus);

      this.userServiceClient.updateApprovalStatus(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          resolve(response);
        } else {
          reject(err);
        }
      });
    });

  public createUser = (emailOrPhone: string, operatingAreaId?: number): Promise<boolean> => {
    const request = new CreateOwnUserRequest();

    if (operatingAreaId) {
      request.setOperatingAreaId(operatingAreaId);
    }

    if (isEmail(emailOrPhone)) {
      request.setEmail(emailOrPhone);
    }

    if (isPhone(emailOrPhone)) {
      request.setPhone(emailOrPhone);
    }
    return new Promise(async (resolve, reject) => {
       this.userServiceClient.createOwnUser(
        request,
        await requestMetadata(this.auth, this.oriApiKey),
        (err, _response) => {
          if (!err) {
            resolve(true);
          } else {
            reject(err);
          }
        });
    });
  }

  public requestSiteAccess = (operatingAreaId: number): Promise<boolean> => {
    const request = new RequestSiteAccessRequest();
    request.setOperatingAreaId(operatingAreaId);

    return new Promise(async (resolve, reject) => {
      this.userServiceClient.requestSiteAccess(
       request,
       await requestMetadata(this.auth, this.oriApiKey),
       (err, _response) => {
         if (!err) {
           resolve(true);
         } else {
           reject(err);
         }
       });
    });
  }

  public updatePushToken = (pushtokenType: PushTokenType, pushtoken: string): Promise<UpdatePushTokenResponse> => 
     new Promise(async (resolve, reject) => {
      const request = new UpdatePushTokenRequest();
      request.setTokenType(pushtokenType);
      request.setPushToken(pushtoken);
      this.userServiceClient.updatePushToken(request, await requestMetadata(this.auth, this.oriApiKey), (err, response) => {
        if (!err) {
          resolve(response);
        } else {
          reject(err);
        }
      });
    });
  



}
